
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { cloneDeep, isEqual } from "lodash-es";

import GET_CONTRACTOR_PROJECTS from "@/apollo/projects/GetContractorProjects.gql";

import CardPagination from "@/components/card/PaginationNew.vue";
import DefaultContentLayout from "@/components/scaffold/DefaultContentLayoutNew.vue";
import ProjectCollectionFilters from "@/components/companyProjects/ProjectCollectionFiltersNew.vue";
import ProjectCard from "@/components/companyProjects/ProjectCardNew.vue";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import EmptyListPlaceholder from "@/components/common/EmptyListPlaceholder.vue";
import CardBodyItem from "@/components/card/CardBodyItem.vue";
import ProjectActivitySmallCard from "@/components/companyProjects/ProjectActivitySmallCard.vue";
import paginationConfig from "@/data/paginationConfig";
import contractorProjectsFiltersTemplate from "@/data/filtersTemplates/contractorProjectsFiltersTemplate";

import getFullNameOrPlaceholder from "@/helpers/getFullNameOrPlaceholder";
import {
  getFiltersDataFromQuery,
  joinQueryValues,
} from "@/helpers/router/prepareFiltersQuery";
import RouteNames from "@/entities/RouteNames";
import PaginationConfig from "@/entities/PaginationConfig";
import ContractorProjectsFiltersQuery from "@/entities/filters/ContractorProjectsFiltersQuery";
import {
  User,
  Company,
  Project,
  Query_RootProjectArgs,
  Timesheet_Bool_Exp,
} from "@/generated/graphql";
import ProjectsFiltersQuery from "@/entities/filters/ProjectsFiltersQuery";
import moment from 'moment';
import {
  getPaginationDataFromQuery,
  getPaginationQuery,
} from "@/helpers/router/preparePaginationQuery";
import {
  getEmptyFiltersResultMessage,
  getEmptyListMessage,
} from "@/helpers/lists/emptyListMessagesNew";
import EmptyListImageProjects from "@/components/icons/emptyListImages/EmptyListImageProjects.vue";
import getStateQueryConditions from "@/helpers/locations/stateQueryConditions";
import RadioButtonGroup from "@/components/common/RadioButtonGroupNew.vue";

const projectStatus = {
  UPCOMING: "UPCOMING",
  NEW_VISITS: "NEW_VISITS",
};

interface ProjectNew extends Project {
  project_status: string;
}

@Component({
  components: {
    EmptyListImageProjects,
    DefaultContentLayout,
    ProjectCollectionFilters,
    EmptyListPlaceholder,
    CardBodyItem,
    ProjectCard,
    CardPagination,
    LoadingOverlay,
    ProjectActivitySmallCard,
    RadioButtonGroup,
  },
  apollo: {
    projects: {
      query: GET_CONTRACTOR_PROJECTS,
      variables() {
        return this.projectFilter;
      },
      update(data) {
        this.paginationConfig.total = data.total.aggregate.count;
        this.paginationConfig.loadedCount = data.project.length;

        this.activeNewVisits = 0;
        this.activeUpcomingVisits = 0;

        const updateProj = data?.project?.filter((proj) => {
          const activities = proj.project_activities || [];
          const now = new Date();

          let projectStatusValue;

          const hasNewVisit = activities.some(
            (a) =>
              new Date(a.start_date) > now &&
              (!a.accepted || a.accepted === null) &&
              !a.completed
          );

          if (hasNewVisit) {
            projectStatusValue = projectStatus.NEW_VISITS;
            this.activeNewVisits++;
          } else {
            const hasUpcoming = activities.some(
              (a) => new Date(a.start_date) > now && !a.completed
            );

            if (hasUpcoming) {
              projectStatusValue = projectStatus.UPCOMING;
              this.activeUpcomingVisits++;
            }
          }

          return this.currentProjectStatus === projectStatusValue;
        });
        return updateProj;
      },
    },
  },
})
export default class ClientProjectsAll extends Vue {
  @Prop({ type: Object, required: true }) readonly currentUser!: User;
  @Prop({ type: Object, required: true }) readonly company!: Company;

  activeNewVisits = 0;
  activeUpcomingVisits = 0;
  projects: ProjectNew[] | null = null;
  timeTrackerOpen: boolean = false;
  paginationConfig: PaginationConfig = { ...paginationConfig };
  routeNames = RouteNames;
  currentProjectStatus = projectStatus.NEW_VISITS;

  get radioOptions() {
    return [
      {
        value: projectStatus.NEW_VISITS,
        label: "New visits",
        total: this.activeNewVisits,
      },
      {
        value: projectStatus.UPCOMING,
        label: "Upcoming visits",
        total: this.activeUpcomingVisits,
      },
    ];
  }

  projectFilters: ContractorProjectsFiltersQuery = cloneDeep(
    contractorProjectsFiltersTemplate
  );

  get loading(): boolean {
    return this.$apollo.loading;
  }

  get emptyListMessage(): string {
    const entitiesName = "clients";
    const filtersEmpty = isEqual(
      this.projectFilters,
      contractorProjectsFiltersTemplate
    );

    if (filtersEmpty) {
      return "You currently do not have any assigned clients";
    }

    return getEmptyFiltersResultMessage(entitiesName);
  }

  get empty(): boolean {
    if (!this.projects) {
      return false;
    }
    return this.projects?.length === 0;
  }

  get projectFilter() {
    const { page, limit } = this.paginationConfig;
    const nextOffset = page * limit - limit;
    const { createdAt, title, managers, location } = this.projectFilters;

    const filterConditions = [];

    if (managers.length) {
      filterConditions.push({
        manager: {
          id: { _in: managers },
        },
      });
    }

    if (title) {
      filterConditions.push({
        client_name: { _ilike: `%${title}%` },
      });
    }

    if (location) {
      filterConditions.push({
        address: { _ilike: `%${location}%` },
      });
    }

    if (createdAt) {
      const startDate = moment(createdAt).utc().startOf("day").toISOString();
      const endDate = moment(createdAt).utc().endOf("day").toISOString();

      const datesConditions = [
        { created_at: { _gte: startDate } },
        { created_at: { _lte: endDate } },
      ];

      filterConditions.push({
        _and: datesConditions,
      });
    }

    return {
      offset: nextOffset,
      limit: limit,
      where: {
        _and: [{ drafted: { _eq: false } }, ...filterConditions],
      },
      projectActivitiesWhere: {
        _and: [
          { drafted: { _eq: false } },
          {
            contractor: {
              user: {
                id: {
                  _eq: this.currentUser.id,
                },
              },
            },
          },
        ],
      },
    };
  }

  get queryPropsToWatch(): string {
    return JSON.stringify([this.projectFilters, this.paginationConfig]);
  }

  get queryParams(): {
    filters: ContractorProjectsFiltersQuery;
    pagination: PaginationConfig;
  } {
    const query = this.$route.query;

    const {
      start_date: startDate,
      end_date: endDate,
      created_at: createdAt,
      title,
      location,
      managers,
      countries,
      states,
      cities,
    } = query;

    const queryData = {
      startDate,
      endDate,
      createdAt,
      title,
      location,
      managers,
      countries,
      states,
      cities,
    };

    const filters = getFiltersDataFromQuery<ContractorProjectsFiltersQuery>(
      contractorProjectsFiltersTemplate,
      queryData
    );
    const pagination = {
      ...this.paginationConfig,
      ...getPaginationDataFromQuery(query),
    };

    return {
      filters,
      pagination,
    };
  }

  get isChosenPageEmpty(): boolean {
    const collectionEmpty = this.projects?.length === 0;
    const notFirstPage = this.paginationConfig.page !== 1;

    return !this.isLoading && collectionEmpty && notFirstPage;
  }

  get isLoading(): boolean {
    return this.$apollo.queries.projects?.loading;
  }

  @Watch("queryPropsToWatch")
  updateQuery(): void {
    if (this.isChosenPageEmpty) {
      return this.showFirstPage();
    }

    const paginationQuery = getPaginationQuery(this.paginationConfig);

    const { createdAt, ...otherFilters } = this.projectFilters;

    const query = joinQueryValues({
      ...paginationQuery,
      ...otherFilters,
      created_at: createdAt,
    });

    if (isEqual(query, this.$route.query)) {
      return;
    }

    this.$router.push({ query }).catch(() => { });
  }

  @Watch("$route.query", { immediate: true })
  onQueryChange(): void {
    const { filters, pagination } = this.queryParams;

    this.projectFilters = filters;
    this.paginationConfig = pagination;
  }

  updateFilters(filters: ProjectsFiltersQuery): void {
    const dataEqual = isEqual(filters, this.projectFilters);

    if (dataEqual) {
      return;
    }
    this.projectFilters = filters;
  }

  resetFilters(): void {
    if (isEqual(this.projectFilters, contractorProjectsFiltersTemplate)) {
      return;
    }

    this.projectFilters = cloneDeep(contractorProjectsFiltersTemplate);
    this.paginationConfig = { ...paginationConfig };
  }

  showFirstPage(): void {
    this.paginationConfig = {
      ...this.paginationConfig,
      page: paginationConfig.page,
    };
  }

  projectCompanyName(project: Project): string {
    return project.company?.business_name || " - ";
  }

  projectManager(project: Project): string {
    const givenName = project?.manager?.user.given_name;
    const familyName = project?.manager?.user.family_name;
    return getFullNameOrPlaceholder(givenName, familyName);
  }

  prepareFilters(filterValues: Record<string, any>): void {
    this.projectFilters = {
      ...this.projectFilters,
      ...filterValues,
    };
  }

  @Watch("currentProjectStatus")
  onProjectStatus() {
    this.$apollo.queries.projects.refetch();
  }

  get currentTotal(): number {
    if (this.currentProjectStatus === projectStatus.NEW_VISITS) {
      return this.activeNewVisits;
    } else if (this.currentProjectStatus === projectStatus.UPCOMING) {
      return this.activeUpcomingVisits;
    }
    return 0;
  }
}
